'use client';
import type {
  FavouriteProduct,
  PaginatedProduct,
  ProductInformation,
} from '@dealerportal/common';
import { setCookie, trackEvent } from '@dealerportal/utilities';
import {
  type ChangeEvent,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface IFavouriteProductsContext {
  favouriteProducts: FavouriteProduct[];
  selectedItems: FavouriteProduct[];
  addRemoveProductsFromFav: (
    productDetails: Omit<FavouriteProduct, 'isSelected'>
  ) => void;
  find: (data: { UPID: string }) => boolean;
  modifySelection: (e: ChangeEvent<HTMLInputElement>) => void;
  open(): void;
  close(): void;
  clear: MouseEventHandler<HTMLButtonElement>;
  dialogRef: RefObject<HTMLDialogElement>;
  favCount: number;
  showPip: boolean; // New state for notification pip
  triggerPip: () => void; // New method to trigger pip
  clearPip: () => void; // New method to clear pip
  loaded: boolean;
}

export const FavouriteProductsContext =
  createContext<IFavouriteProductsContext>(Object.assign({}));

export function FavouriteProductsContextProvider({
  clientId,
  children,
}: {
  children: ReactNode;
  clientId: number;
}) {
  const [favourites, setFavourites] = useState<FavouriteProduct[]>([]);
  const [showPip, setShowPip] = useState<boolean>(false);
  const [loaded, setLoaded] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const storedClientId = localStorage.getItem('clientId');
    // To Clear if clientid does not match with the favourites list, clear the old list to prevent showing products from another client!!
    if (!storedClientId) {
      localStorage.setItem('clientId', clientId.toString());
    } else if (Number.parseInt(storedClientId) !== clientId) {
      localStorage.removeItem('favourites');
      localStorage.removeItem('showPip');
      localStorage.setItem('clientId', clientId.toString());
    }
    const storedFavourites = localStorage.getItem('favourites');

    if (storedFavourites) {
      const parsedFavourites = JSON.parse(storedFavourites);
      if (Array.isArray(parsedFavourites) && parsedFavourites.length) {
        setFavourites(parsedFavourites);
      }
    }
    const storedShowPip = localStorage.getItem('showPip');
    if (storedShowPip) {
      setShowPip(JSON.parse(storedShowPip));
    }
  }, []);

  const favCount = useMemo(() => favourites.length, [favourites]);
  const selectedItems = useMemo(
    () => favourites.filter((item) => item.isSelected),
    [favourites]
  );

  const addRemoveProductsFromFav = (
    productDetails: Omit<FavouriteProduct, 'isSelected'>
  ) => {
    setFavourites((prevFavourites) => {
      const foundIndex = prevFavourites.findIndex(
        (product) =>
          product.UPID === productDetails.UPID &&
          product?.colorName === productDetails?.colorName
      );
      const newFavourites = [...prevFavourites];
      const selectedItems = newFavourites.filter((item) => item.isSelected);

      if (foundIndex > -1) {
        trackEvent('FAVORITE_REMOVED', prevFavourites[foundIndex]);
        newFavourites.splice(foundIndex, 1);
      } else {
        trackEvent('FAVORITE_ADDED', productDetails);
        newFavourites.push({
          ...(productDetails as ProductInformation | PaginatedProduct),
          isSelected: !(selectedItems.length >= 4),
        });
        triggerPip();
      }
      if (newFavourites.length === 0) {
        clearPip();
      }

      localStorage.setItem('favourites', JSON.stringify(newFavourites));
      setCookie(
        'favorites',
        JSON.stringify(newFavourites.map((item) => item.UPID))
      );
      return newFavourites;
    });
  };

  const open = () => {
    setLoaded(true);

    const dialogElement = dialogRef.current;

    if (dialogElement && !dialogElement.open) {
      dialogElement.showModal();
    }
  };

  const close = () => {
    setLoaded(false);

    const dialogElement = dialogRef.current;

    if (dialogElement?.open) {
      dialogElement.close();
    }
  };

  const find = ({ UPID }: { UPID: string }) => {
    if (!UPID) {
      return false;
    }

    return favourites.some((product) => product.UPID === UPID);
  };

  const modifySelection = (e: ChangeEvent<HTMLInputElement>) => {
    setFavourites((prevFavourites) => {
      const foundIndex = prevFavourites.findIndex(
        (product) => product.UPID === e.target.value
      );
      const newFavourites = [...prevFavourites];
      const selectedItems = newFavourites.filter((item) => item.isSelected);

      if (
        foundIndex > -1 &&
        newFavourites[foundIndex].isSelected !== e.target.checked
      ) {
        newFavourites[foundIndex].isSelected =
          selectedItems.length < 4 ? e.target.checked : false;
        localStorage.setItem('favourites', JSON.stringify(newFavourites));
      }

      return newFavourites;
    });
  };

  const clear: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setFavourites([]);
    localStorage.removeItem('favourites');
    clearPip();
  };
  const triggerPip = () => {
    setShowPip(true);
    localStorage.setItem('showPip', JSON.stringify(true));
  };

  const clearPip = () => {
    setShowPip(false);
    localStorage.setItem('showPip', JSON.stringify(false));
  };
  const value: IFavouriteProductsContext = {
    favouriteProducts: favourites,
    addRemoveProductsFromFav,
    open,
    close,
    find,
    dialogRef: dialogRef as RefObject<HTMLDialogElement>,
    favCount,
    modifySelection,
    clear,
    selectedItems,
    showPip,
    triggerPip,
    clearPip,
    loaded,
  };

  return (
    <FavouriteProductsContext.Provider value={value}>
      {children}
    </FavouriteProductsContext.Provider>
  );
}
